import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, setDoc, onSnapshot, query, orderBy, limit } from 'firebase/firestore';

// AI 분석 (2026-09-30) — 데이터 분석 화면의 "AI 분석" 탭.
// 관리자가 요청(E2B_AI_REQUESTS)을 만들면 서버 함수(functions/index.js crmAiReport)가 보고서를
// E2B_AI_REPORTS 에 저장한다. 직원은 저장된 최신 보고서를 읽기만 한다. 월 한도는 서버가 센다(E2B_AI_USAGE).
const REQUEST_COLLECTION = 'E2B_AI_REQUESTS';
const REPORT_COLLECTION = 'E2B_AI_REPORTS';
const USAGE_COLLECTION = 'E2B_AI_USAGE';
export const MONTHLY_LIMIT = 30;

/**
 * 이번 달(한국 시각) 키 "YYYY-MM".
 * @returns {string} 달 키
 */
const monthKey = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit' }).format(new Date()).slice(0, 7);

export const aiService = {
  /**
   * 최근 보고서와 질문·답을 읽는다(한 컬렉션에 type 으로 섞여 있다 — 예전 보고서는 type 이 없음).
   * @returns {Promise<{report: object|null, questions: object[]}>} 최신 보고서와 최근 질문(최신 먼저)
   */
  fetchRecent: async () => {
    const snap = await getDocs(query(collection(db, REPORT_COLLECTION), orderBy('createdAt', 'desc'), limit(30)));
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { report: rows.find((r) => r.type !== 'question') || null, questions: rows.filter((r) => r.type === 'question') };
  },

  /**
   * 이번 달 사용 횟수.
   * @returns {Promise<number>} 횟수
   */
  fetchMonthlyCount: async () => {
    const snap = await getDoc(doc(db, USAGE_COLLECTION, monthKey()));
    return snap.exists() ? Number(snap.data().count) || 0 : 0;
  },

  /**
   * 보고서 작성 또는 자유 질문을 요청한다(관리자만 — 규칙이 강제).
   * @param {{id: string, name?: string}} user 로그인 사용자
   * @param {string} [question] 자유 질문(1~500자). 비우면 정해진 보고서
   * @returns {Promise<string>} 요청 id
   */
  requestReport: async (user, question) => {
    const ref = doc(collection(db, REQUEST_COLLECTION));
    const data = { requestedByUid: user.id, requestedByName: user.name || '', status: 'queued', createdAt: new Date().toISOString() };
    // 질문이 있으면 자유 질문, 없으면 정해진 보고서(AS 매출·부품 재고·보증 만료).
    if (question) data.question = question;
    await setDoc(ref, data);
    return ref.id;
  },

  /**
   * 요청의 진행 상태를 지켜본다.
   * @param {string} id 요청 id
   * @param {function(object): void} onChange 상태가 바뀔 때(status: queued/running/done/error, error)
   * @returns {function(): void} 그만 보기
   */
  watchRequest: (id, onChange) => onSnapshot(doc(db, REQUEST_COLLECTION, id),
    (snap) => { if (snap.exists()) onChange(snap.data()); },
    () => onChange({ status: 'error', error: '진행 상태를 읽지 못했습니다.' }))
};
